import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { plans as plansApi } from '../api/client.js';
import { downloadExport } from '../api/campaign.js';
import StopItem from './StopItem.jsx';
import {
  ArrowLeftIcon, CalendarIcon, NavigateIcon,
  CopyIcon, DocumentIcon, TrashIcon,
} from '../common/icons.jsx';

export default function PlanDetail() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [dupDate, setDupDate] = useState(todayStr());
  const dragIndex = useRef(null);

  const load = async () => {
    try {
      const data = await plansApi.get(planId);
      setPlan(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [planId]);

  const handleDragStart = (_e, index) => { dragIndex.current = index; };
  const handleDragOver = (e) => { e.preventDefault(); };

  const handleDrop = async (_e, dropIndex) => {
    if (dragIndex.current === null || dragIndex.current === dropIndex) return;
    const stops = [...plan.stops];
    const [moved] = stops.splice(dragIndex.current, 1);
    stops.splice(dropIndex, 0, moved);
    dragIndex.current = null;
    const reordered = stops.map((s, i) => ({ ...s, stop_order: i + 1 }));
    setPlan((prev) => ({ ...prev, stops: reordered }));
    try {
      await plansApi.reorder(plan.id, reordered.map((s) => ({ id: s.id, stopOrder: s.stop_order })));
      await load();
    } catch (err) {
      setError(err.message);
      await load();
    }
  };

  const handleStatusUpdate = async (stopId, data) => {
    try {
      await plansApi.updateStop(plan.id, stopId, data);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await plansApi.delete(plan.id);
      navigate('/plans');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDuplicate = async () => {
    setShowDuplicate(false);
    try {
      const dup = await plansApi.duplicate(plan.id, dupDate);
      navigate(`/plans/${dup.id}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMoveStop = async (stopId, targetPlanId) => {
    try {
      await plansApi.moveStop(stopId, targetPlanId);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePdfReport = () => {
    downloadExport(`plan-report/${planId}.pdf`, `rapport-tournée-${planId}.pdf`);
  };

  if (loading) return <LoadingSpinner />;
  if (error && !plan) return <ErrorMsg text={error} />;
  if (!plan) return <ErrorMsg text="Tournée introuvable" />;

  const stopsWithCoords = plan.stops.filter((s) => s.lat && s.lng);
  const itineraryUrl = buildItineraryUrl(stopsWithCoords);
  const formatDate = (d) =>
    new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const deliveredCount = plan.stops.filter((s) => s.status === 'delivered').length;
  const progress = plan.stops.length > 0
    ? Math.round((deliveredCount / plan.stops.length) * 100) : 0;

  const driverInitials = (plan.driver_name || '?')
    .split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="space-y-4 overflow-x-hidden">
      <Link to="/plans"
        className="inline-flex items-center gap-1.5 text-sm text-[#1e40af] hover:text-[#0f172a] transition-colors min-h-[44px]">
        <ArrowLeftIcon size={16} />
        Retour tournées
      </Link>

      {/* Header card */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CalendarIcon size={18} className="text-[#1e40af]" />
              <h2 className="text-xl font-bold text-[#0f172a] capitalize">{formatDate(plan.date)}</h2>
            </div>
            <p className="text-sm text-[#64748b]">{plan.stops.length} arrêts - {plan.total_units} unités</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-[#1e40af] text-white flex items-center justify-center text-sm font-bold">
              {driverInitials}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-[#64748b] mb-1.5">
            <span>{deliveredCount}/{plan.stops.length} livrés</span>
            <span className="font-bold text-[#059669]">{progress}%</span>
          </div>
          <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#059669] to-emerald-400 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
          <ActionBtn icon={<CopyIcon size={15} />} label="Dupliquer" onClick={() => setShowDuplicate(true)} color="blue" />
          <ActionBtn icon={<DocumentIcon size={15} />} label="PDF" onClick={handlePdfReport} color="green" />
          <ActionBtn icon={<TrashIcon size={15} />} label="Supprimer" onClick={() => setShowDeleteConfirm(true)} color="red" />
        </div>
      </div>

      {/* Full itinerary button */}
      {itineraryUrl && (
        <a href={itineraryUrl} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#059669] to-emerald-500 hover:from-[#047857] hover:to-emerald-600 text-white rounded-xl p-4 font-medium text-sm transition-all min-h-[44px] shadow-sm">
          <NavigateIcon size={18} />
          Itinéraire complet Google Maps
        </a>
      )}

      {error && <p className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      {/* Stops timeline */}
      <div className="relative">
        {/* Vertical connector line */}
        {plan.stops.length > 1 && (
          <div className="absolute left-[23px] top-4 bottom-4 w-0.5 bg-gray-200 z-0" />
        )}
        <div className="space-y-3 relative z-10">
          {plan.stops.map((stop, index) => (
            <StopItem key={stop.id} stop={stop} index={index} planId={plan.id}
              onDragStart={handleDragStart} onDragOver={handleDragOver}
              onDrop={handleDrop} onStatusUpdate={handleStatusUpdate}
              onMoveStop={handleMoveStop}
              onMoveUp={index > 0 ? () => { dragIndex.current = index; handleDrop(null, index - 1); } : null}
              onMoveDown={index < plan.stops.length - 1 ? () => { dragIndex.current = index; handleDrop(null, index + 1); } : null}
            />
          ))}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center space-y-4">
            <p className="text-lg font-bold text-[#0f172a]">Supprimer cette tournée ?</p>
            <p className="text-sm text-[#64748b]">Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 border border-gray-300 rounded-xl py-3 font-medium">Annuler</button>
              <button onClick={handleDelete} className="flex-1 bg-red-600 text-white rounded-xl py-3 font-medium">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate modal */}
      {showDuplicate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
            <p className="text-lg font-bold text-[#0f172a]">Dupliquer la tournée</p>
            <div>
              <label className="block text-xs font-medium text-[#64748b] mb-1.5">Date de la copie</label>
              <input type="date" value={dupDate} onChange={(e) => setDupDate(e.target.value)}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowDuplicate(false)} className="flex-1 border border-gray-300 rounded-xl py-3 font-medium">Annuler</button>
              <button onClick={handleDuplicate} className="flex-1 bg-blue-600 text-white rounded-xl py-3 font-medium">Dupliquer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Small action button with icon.
 * @param {{ icon: JSX.Element, label: string, onClick: () => void, color: string }} props
 */
function ActionBtn({ icon, label, onClick, color }) {
  const colors = {
    blue: 'text-[#1e40af] bg-blue-50 hover:bg-blue-100',
    green: 'text-[#059669] bg-emerald-50 hover:bg-emerald-100',
    red: 'text-[#dc2626] bg-red-50 hover:bg-red-100',
  };
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors min-h-[36px] ${colors[color]}`}>
      {icon} {label}
    </button>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin h-8 w-8 border-4 border-[#1e40af] border-t-transparent rounded-full" />
    </div>
  );
}

/** @param {{ text: string }} props */
function ErrorMsg({ text }) {
  return (
    <div className="flex items-center justify-center py-12">
      <p className="text-red-600 bg-red-50 rounded-xl px-6 py-4">{text}</p>
    </div>
  );
}

/** @returns {string} */
function todayStr() {
  return new Date().toISOString().split('T')[0];
}

/**
 * @param {{ lat: number, lng: number }[]} stops
 * @returns {string|null}
 */
function buildItineraryUrl(stops) {
  if (stops.length < 2) return null;
  const origin = `${stops[0].lat},${stops[0].lng}`;
  const dest = `${stops[stops.length - 1].lat},${stops[stops.length - 1].lng}`;
  const waypoints = stops.slice(1, -1).slice(0, 10)
    .map((s) => `${s.lat},${s.lng}`).join('|');
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}`;
  if (waypoints) url += `&waypoints=${waypoints}`;
  url += '&travelmode=driving';
  return url;
}
